import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import Tag from '@components/tag/Tag'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'

import { ControleFormScheme, ControleFormFields } from '../scheme'
import * as ControleStore from '@store/controle/controle'

const FormLayout = ({ step, form, onNext, onBack, onQuit, children, full, last }: ControleLayoutProps) => {
  const i18n = useI18n()

  const [valid, setValid] = React.useState(false)

  const fields = React.useMemo(() => ControleFormFields[step].map((key) => form[key]), [form, step])

  React.useEffect(() => {
    const schema = ControleFormScheme[step as keyof typeof ControleFormScheme]

    if (schema) {
      schema
        .validate(form)
        .then(() => setValid(true))
        .catch((err) => setValid(false))
    }
  }, [fields, step])

  return (
    <MainContainer>
      {full ? (
        <Column>{children}</Column>
      ) : (
        <DualLine>
          <Column left>
            <TagContainer>
              <Tag
                activeColor
                focused
                item={{ label: i18n.t('screens.controles.form.quit'), value: 'tag' }}
                selected={false}
                leftIcon="close"
                onItemSelected={onQuit}
              />
            </TagContainer>

            <Animators>
              <SubTitle>{i18n.t('screens.controles.title')}</SubTitle>
              <Title>{i18n.t(`screens.controles.form.${step}.title`, form)}</Title>
            </Animators>
          </Column>

          <Column>{children}</Column>
        </DualLine>
      )}

      <Buttons>
        <ButtonContainer>
          <Button
            label={i18n.t('screens.incident.previous')}
            status="active"
            onClick={onBack}
            textOnly
            color="light"
            textColor={theme.colors.primaryText}
          />
        </ButtonContainer>

        {step === 'spaces' ? (
          <>
            <ButtonContainer>
              <Button
                label={i18n.t('screens.controles.form.spaces.nextAllSpaces')}
                status={valid ? 'active' : 'disabled'}
                onClick={() => {
                  const update = form.answerMode === 'allSpaces' ? {} : { reponses: [] }
                  ControleStore.actions.setForm({ answerMode: 'allSpaces', ...update })
                  onNext()
                }}
              />
            </ButtonContainer>
            <ButtonContainer>
              <Button
                label={i18n.t('screens.controles.form.spaces.nextEachSpace')}
                status={valid ? 'active' : 'disabled'}
                onClick={() => {
                  const update = form.answerMode === 'eachSpace' ? {} : { reponses: [] }
                  ControleStore.actions.setForm({ answerMode: 'eachSpace', ...update })
                  onNext()
                }}
              />
            </ButtonContainer>
          </>
        ) : (
          <ButtonContainer>
            <Button
              label={i18n.t(last ? 'screens.controles.form.finish' : 'common.validate')}
              status={valid ? 'active' : 'disabled'}
              onClick={onNext}
            />
          </ButtonContainer>
        )}
      </Buttons>
    </MainContainer>
  )
}

export default FormLayout

const MainContainer = styled(View)`
  flex: 1;
  flex-direction: column;
`

const DualLine = styled(View)`
  flex: 1;
  flex-direction: row;
`

const Column = styled(View)<{ left?: boolean }>`
  flex: 1;
  flex-direction: column;
  background-color: ${(props) => (props.left ? props.theme.colors.cardSecondary : props.theme.colors.background)};
  position: relative;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 20px;
  margin: 0 24px;
  margin-bottom: 15px;
  margin-top: 125px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
  margin: 0 24px;
`

const Buttons = styled(View)`
  flex-direction: row;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  width: 275px;
  margin: 12px;
`

const TagContainer = styled(View)`
  position: absolute;
  top: 24px;
  left: 24px;
`
